import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { MainSliderComponent } from '../../components/main-slider/main-slider.component';
import { AuctionsComponent } from '../../components/auctions/auctions.component';
import { AboutUsComponent } from '../../components/about-us/about-us.component';
import { OurClientsComponent } from '../../components/our-clients/our-clients.component';
import { JoinUsComponent } from '../../components/join-us/join-us.component';
import { BlogsComponent } from '../../components/blogs/blogs.component';
import { BuyerFooterComponent } from '../../../../Shared/components/buyer-footer/buyer-footer.component';
import { isPlatformBrowser, NgClass, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuctionsStatisticsComponent } from '../../components/auctions-statistics/auctions-statistics.component';
import { HowProcessComponent } from '../../components/how-process/how-process.component';
import { SystemFeaturesComponent } from '../../components/system-features/system-features.component';
import { LiveHorsesComponent } from '../../components/live-horses/live-horses.component';
import { InternationalAuctionsBandComponent } from '../../components/international-auctions-band/international-auctions-band.component';
import { LandingService } from '../../_services/landing.service';
import { ToastService } from 'src/app/Shared/services/toast/toast.service';
import {
  EMPTY,
  Subject,
  Subscription,
  auditTime,
  catchError,
  filter,
  finalize,
  map,
  merge,
  switchMap,
  tap,
} from 'rxjs';

import { BuyerAuthService } from 'src/app/app-modules/buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';
import { StorageService } from 'src/app/Core/_services/storage/storage.service';
import { JqueryInitService } from 'src/app/Core/_services/jquery/jquery-init.service';
import { PusherService } from 'src/app/Shared/services/pusher/pusher.service';
import { IAuctionUpdateEventPayload } from 'src/app/Shared/_models/pusher-events-payload.model';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';
import { environment } from 'src/environments/environment';
import { AuctionService } from 'src/app/app-modules/buyer/buyer-modules/buyer-auctions/_services/auction/auction.service';

@Component({
  selector: 'app-landing-page',
  standalone: true,
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
  imports: [
    MainSliderComponent,
    AboutUsComponent,
    BlogsComponent,
    AuctionsStatisticsComponent,
    HowProcessComponent,
    SystemFeaturesComponent,
    LiveHorsesComponent,
    InternationalAuctionsBandComponent,
    AsyncPipe,
  ],
})
export class LandingPageComponent implements OnInit, OnDestroy {
  isBrowser!: boolean;
  currentDateTime: any;
  isLoading: boolean = false;
  searchVal: string = '';
  filterBy!: string;
  auctions!: any[];
  private subscriptions = new Subscription();
  private forceRefresh$ = new Subject<boolean>();
  private auctionUpdates$ = new Subject<IAuctionUpdateEventPayload>();
  private sliderInitTimer: ReturnType<typeof setTimeout> | null = null;
  currentLang: any;
  loggedIn = false;
  translations: any;
  user_id: any;
  calledFirstTime!: boolean;
  isHomeLiveSectionShown: boolean = true;
  private hasRequestedInitialDynamicRefresh = false;
  constructor(
    private route: ActivatedRoute,
    private titleService: Title,
    @Inject(PLATFORM_ID) private platformId: any,
    private homeService: LandingService,
    private toastService: ToastService,
    private translate: TranslateService,
    private auth: BuyerAuthService,
    private storage: StorageService,
    private initJQuery: JqueryInitService,
    private pusherService: PusherService,
    public localeService: LocaleService,
    private auctionService: AuctionService
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.isHomeLiveSectionShown = environment.isHomeLiveSectionShown;
  }
  windowScrolled = false;
  data: any = null;
  private onlineListener = this.handleOnline.bind(this);
  private scrollListener: (() => void) | null = null;
  ngOnInit() {
    this.setupHomeRefreshPipeline();

    if (this.isBrowser) {
      this.currentLang = JSON.parse(
        localStorage.getItem(this.storage.getPrefixedKey('selectedLang')) ??
          '{}'
      );
      this.subscriptions.add(
        this.auth.loggedIn_authorized$.subscribe((res) => {
          this.loggedIn = res;
          this.user_id = this.storage?.userData?.id;
          if (this.calledFirstTime) {
            // BXS-3588: Reset the dynamic-refresh flag so the per-buyer
            // settings refresh (POST /buyer/auction/AuctionItemsSettings)
            // re-runs after the bootstrap re-fetch. Otherwise the home
            // page re-renders with loggedIn=true but auction_settings
            // missing from each item (the bootstrap response doesn't
            // include it and sanitizeBootstrapData strips the per-buyer
            // dynamic fields), so the "Request to Bid" button on Live
            // Auctions cards disappears entirely after a popup-login
            // until the user manually refreshes the page.
            this.hasRequestedInitialDynamicRefresh = false;
            this.requestHomeData(false);
          }
        })
      );
      this.scrollListener = () => {
        this.windowScrolled = window.pageYOffset !== 0;
      };
      window.addEventListener('scroll', this.scrollListener, { passive: true });
      window.addEventListener('online', this.onlineListener);

      // Subscribe to auction updates once on init
      this.subscribeToAuctionUpdatesChannels();
    }

    this.requestHomeData();
  }

  private setupHomeRefreshPipeline() {
    const forceRefreshStream$ = this.forceRefresh$;

    const auctionRefreshStream$ = this.auctionUpdates$.pipe(
      filter((eventData) => this.isAuctionDisplayed(eventData?.auction_id)),
      auditTime(3000),
      filter(() => !this.isBrowser || document.visibilityState === 'visible'),
      map(() => false)
    );

    this.subscriptions.add(
      merge(forceRefreshStream$, auctionRefreshStream$)
        .pipe(switchMap((load) => this.loadHomeData$(load)))
        .subscribe()
    );
  }

  private requestHomeData(load = true) {
    this.forceRefresh$.next(load);
  }

  private loadHomeData$(load = true) {
    this.calledFirstTime = true;
    this.toastService.clear();
    if (load) {
      this.isLoading = true;
    }
    return this.homeService
      .getHomepageBootstrap({
        locale: this.localeService.languageCode || 'en',
        page: 1,
        per_page: 10,
      })
      .pipe(
        tap((response: any) => {
          this.currentDateTime = response?.current_datetime;
          this.data = this.sanitizeBootstrapData(response?.data);
          this.scheduleAuctionSliderInit();

          if (this.isBrowser && !this.hasRequestedInitialDynamicRefresh) {
            this.hasRequestedInitialDynamicRefresh = true;
            this.getData(false);
          }
        }),
        catchError((error) => {
          this.toastService.showError(
            error?.error?.Error?.[0] ?? 'An error occurred while fetching Data.'
          );
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        })
      );
  }

  getData(load = true) {
    const auctionItemIds = this.extractAuctionItemIds();
    if (auctionItemIds.length === 0) {
      return;
    }

    this.toastService.clear();
    if (load) {
      this.isLoading = true;
    }

    this.auctionService
      .getAuctionItemsSettings({
        auction_item_ids: auctionItemIds,
      })
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.currentDateTime = response?.current_datetime;
          this.data = this.mergeDynamicHomeData(response?.data);
          this.scheduleAuctionSliderInit();
        },
        error: (error) => {
          this.isLoading = false;
          this.toastService.showError(
            error?.error?.Error?.[0] ?? 'An error occurred while fetching Data.'
          );
        },
      });
  }

  private extractAuctionItemIds(): number[] {
    const auctionItems = Array.isArray(this.data?.auction_items)
      ? this.data.auction_items
      : [];

    return Array.from(
      new Set(
        auctionItems
          .map((item: any) => Number(item?.id))
          .filter((id: number) => Number.isInteger(id) && id > 0)
      )
    );
  }

  private mergeDynamicHomeData(dynamicData: any): any {
    const currentData = this.data ?? {};
    const currentItems = Array.isArray(currentData.auction_items)
      ? currentData.auction_items
      : [];

    const dynamicItems = dynamicData?.auction_items ?? {};
    const dynamicAuctions = dynamicData?.auctions ?? {};
    const mergedItems = currentItems.map((item: any) => {
      const itemId = item?.id;
      const auctionId = item?.auction_id ?? item?.auction?.id;
      const dynamicItem =
        dynamicItems?.[String(itemId)] ?? dynamicItems?.[itemId] ?? null;
      const dynamicAuctionSettings =
        dynamicItem?.auction_settings ??
        dynamicAuctions?.[String(auctionId)] ??
        dynamicAuctions?.[auctionId] ??
        null;

      if (!dynamicItem && !dynamicAuctionSettings) {
        return item;
      }

      return {
        ...item,
        auction_settings: dynamicAuctionSettings ?? item?.auction_settings,
        auction_item_settings:
          dynamicItem?.auction_item_settings ?? item?.auction_item_settings,
        highest_bid: dynamicItem?.highest_bid ?? item?.highest_bid,
        make_an_offer: dynamicItem?.make_an_offer ?? item?.make_an_offer,
        status: dynamicItem?.status ?? item?.status,
        display_price: dynamicItem?.display_price ?? item?.display_price,
        buyer_name: dynamicItem?.buyer_name ?? item?.buyer_name,
        is_under_reserve:
          dynamicItem?.is_under_reserve ?? item?.is_under_reserve,
        bids_count: dynamicItem?.bids_count ?? item?.bids_count,
        bidders_count: dynamicItem?.bidders_count ?? item?.bidders_count,
        status_override: dynamicItem?.status_override ?? item?.status_override,
        buyer_name_override:
          dynamicItem?.buyer_name_override ?? item?.buyer_name_override,
        price_override: dynamicItem?.price_override ?? item?.price_override,
        winner: dynamicItem?.winner ?? item?.winner,
        auction_item_winner_id:
          dynamicItem?.auction_item_winner_id ?? item?.auction_item_winner_id,
        unsold_flag_at: dynamicItem?.unsold_flag_at ?? item?.unsold_flag_at,
        offer_window_ended_at:
          dynamicItem?.offer_window_ended_at ?? item?.offer_window_ended_at,
        buyer_status: dynamicItem?.buyer_status ?? item?.buyer_status,
        rna_status:
          typeof dynamicItem?.status === 'string'
            ? dynamicItem.status === 'rna'
            : (item?.rna_status ?? false),
        auction: {
          ...(item?.auction ?? {}),
          buyer_status: dynamicItem?.buyer_status ?? item?.auction?.buyer_status,
          buyer_accepted_seller_terms:
            dynamicItem?.buyer_accepted_seller_terms ??
            item?.auction?.buyer_accepted_seller_terms,
        },
      };
    });

    return {
      ...currentData,
      auction_items: mergedItems,
    };
  }

  private sanitizeBootstrapData(rawData: any): any {
    const data = rawData ?? {};
    const auctionItems = Array.isArray(data?.auction_items) ? data.auction_items : [];

    const sanitizedItems = auctionItems.map((item: any) => {
      if (!item || typeof item !== 'object') {
        return item;
      }

      const {
        status,
        rna_status,
        highest_bid,
        make_an_offer,
        display_price,
        buyer_name,
        is_under_reserve,
        bids_count,
        bidders_count,
        status_override,
        buyer_name_override,
        price_override,
        winner,
        auction_item_winner_id,
        unsold_flag_at,
        offer_window_ended_at,
        buyer_status,
        ...rest
      } = item;

      return {
        ...rest,
        auction: {
          ...(rest?.auction ?? {}),
          buyer_status: null,
          buyer_accepted_seller_terms: null,
        },
      };
    });

    return {
      ...data,
      auction_items: sanitizedItems,
    };
  }

  private scheduleAuctionSliderInit() {
    if (this.sliderInitTimer) {
      clearTimeout(this.sliderInitTimer);
    }

    this.sliderInitTimer = setTimeout(() => {
      const auctionItems = Array.isArray(this.data?.auction_items)
        ? this.data.auction_items
        : [];
      this.initJQuery.initAuctionSlider(this.localeService.dir, auctionItems);
    }, 100);
  }

  private isAuctionDisplayed(auctionId: number | undefined): boolean {
    if (auctionId == null || !Array.isArray(this.data?.auction_items)) {
      return false;
    }

    return (
      this.data.auction_items.findIndex(
        (item: any) => String(item?.auction?.id) === String(auctionId)
      ) > -1
    );
  }
  scrollToTop(): void {
    if (this.isBrowser) {
      window.scrollTo(0, 0);
    }
  }
  handleOnline() {
    if (this.isBrowser && document.visibilityState !== 'visible') {
      return;
    }

    setTimeout(() => {
      this.requestHomeData(false);
    }, 500);
  }
  handleAuctionUpdate(data: IAuctionUpdateEventPayload) {
    this.auctionUpdates$.next(data);
  }
  subscribeToAuctionUpdatesChannels() {
    this.pusherService.subscribe(
      'public-auction',
      'auction.updated',
      false,
      'Auction Updated',
      (data: any) => {
        this.handleAuctionUpdate(data);
      }
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    this.forceRefresh$.complete();
    this.auctionUpdates$.complete();

    if (this.sliderInitTimer) {
      clearTimeout(this.sliderInitTimer);
      this.sliderInitTimer = null;
    }

    if (this.isBrowser) {
      if (this.scrollListener) {
        window.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
      }
      window.removeEventListener('online', this.onlineListener);
      this.pusherService.unsubscribeFromChannel('public-auction');
      // Clean up slider instance to prevent memory leaks
      this.initJQuery.destroyAuctionSlider();
    }
  }
}
